import { Pagination } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { endpoint } from '..';
import * as Alert from '../../../components/Alert/Alert';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import { IDropdownOption } from '../../../components/Dropdown/IDropdown';
import Spinner from '../../../components/Spinner/Spinner';
import { IInitialState } from '../../../reduxService';
import { useAppDispatch, useAppSelector } from '../../../reduxService/hooks';
import TemplateProject from '../../MACRA_1/components/templateProject/TemplateProject';
import { IProjectMacra1 } from '../../MACRA_1/store/project/projectSlice';
import { IProjectMacra2 } from '../../MACRA_2/store/project/projectSlice';
import { getActionByProjectType } from '../../actionMap';
import { IGetByTypeRequest } from '../../api/IApiRequest';
import Layout from '../../components/Layout/Layout';
import { EProjectType, UnitOfMeasure } from '../../enum/project';
import { selectProjectInfo, setProjectInfo } from '../../store/projectInfo/projectInfoSlice';
import { ITemplateProps } from './ITemplate';

const MIN_TEMPLATE_PAGE_SIZE = 6;

const Template = (props: ITemplateProps) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const projectInfo = useAppSelector(selectProjectInfo);

    const [page, setPage] = useState<number>(1);
    const [unitOfMeasure, setUnitOfMeasure] = useState<number>(UnitOfMeasure.SI);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [projectType, setProjectType] = React.useState<EProjectType>(projectInfo.data?.type ?? EProjectType.Macra1);
    const { selectTemplate, selectProject, fetchAllTemplate, fetchTemplateById, cleanResults, cleanProject } = getActionByProjectType();

    const project: IInitialState<IProjectMacra1 | IProjectMacra2> = useAppSelector(selectProject as any);
    const template = useAppSelector(selectTemplate);

    useEffect(() => {
        fetchTemplates(1);
    }, []);

    const options: IDropdownOption[] = [
        { label: t('metric_unit_system'), value: UnitOfMeasure.SI },
        { label: t('imperial_unit_system'), value: UnitOfMeasure.Imperial },
    ];

    let _templateId: number | undefined;

    const fetchTemplates = async (page: number) => {
        let request: IGetByTypeRequest = {
            type: projectInfo.data!.type!,
            pageNumber: page,
            pageSize: MIN_TEMPLATE_PAGE_SIZE,
        };
        dispatch(fetchAllTemplate(request));
    };

    if (!!template.data && totalPages == 0) {
        setTotalPages(template.data?.pagesCount);
    }

    const handleChangeDropdown = (e: React.ChangeEvent<HTMLSelectElement>) => {
        let target = e.target;
        setUnitOfMeasure(parseInt(target.value));
    };

    const handleChangeTemplate = (templateId: number) => {
        _templateId = templateId;
    };

    const handleClick = () => {
        const handleTemplateSelection = async () => {
            dispatch(cleanResults());
            dispatch(cleanProject());
            const templateIdToLoad = _templateId == undefined ? template.data!.data[0].id : _templateId;
            const fetchTemplateAction: any = dispatch(fetchTemplateById({ id: templateIdToLoad }) as any);
            try {
                await fetchTemplateAction.unwrap();
            } catch {
                Alert.warning(t('error_page_msg') as string);
                return;
            }
            dispatch(setProjectInfo({ ...projectInfo.data!, unitOfMeasure: unitOfMeasure }));
            navigate(endpoint.designCondition);
        };

        if (!project.initialState && project.data?.geometry) {
            Alert.warning(t('template_alert_title'), t('template_alert_body') as string, true).then(result => {
                if (result.isConfirmed) {
                    handleTemplateSelection();
                }
            });
        } else {
            handleTemplateSelection();
        }
    };
    const handleChangeType = (type: number) => {
        const handleProjectTypeSelection = () => {
            setProjectType(type);
            let request: IGetByTypeRequest = {
                type: type,
                pageNumber: 1,
                pageSize: MIN_TEMPLATE_PAGE_SIZE,
            };
            setPage(1);
            setTotalPages(0);
            dispatch(fetchAllTemplate(request));
            dispatch(cleanResults());
            dispatch(cleanProject());
            dispatch(setProjectInfo({ ...projectInfo.data!, type }));
        };

        if (!project.initialState && project.data) {
            Alert.warning(t('template_alert_title'), t('template_alert_body') as string, true).then(result => {
                if (result.isConfirmed) {
                    handleProjectTypeSelection();
                }
            });
        } else {
            handleProjectTypeSelection();
        }
    };

    const handlePageChange = (page: number) => {
        fetchTemplates(page);
        setPage(page);
    };

    let divClasses = page <= totalPages && totalPages !== 1 ? 'd-flex justify-content-between mt-3' : 'd-flex justify-content-end mt-3';
    if (projectInfo.initialState) {
        return <Navigate to={endpoint.home} replace={true} />;
    }
    return (
        <Layout>
            <div className='listContainer'>
                {projectType >= EProjectType.Macra2 && (
                    <div className='d-flex justify-content-between'>
                        <Button
                            disabled={template.pending}
                            classNameBtn={`btn-color-inverted ${projectType === (EProjectType.Macra2 | EProjectType.VerticalDrop) ? 'active' : ''}`}
                            onClick={() => handleChangeType(EProjectType.Macra2 | EProjectType.VerticalDrop)}>
                            {t('project_type_structure_vertical_drop')}
                        </Button>
                        <Button
                            disabled={template.pending}
                            classNameBtn={`btn-color-inverted ${projectType === (EProjectType.Macra2 | EProjectType.SteppedWeir) ? 'active' : ''}`}
                            onClick={() => handleChangeType(EProjectType.Macra2 | EProjectType.SteppedWeir)}>
                            {t('project_type_structure_stepped_weir')}
                        </Button>
                        <Button
                            disabled={template.pending}
                            classNameBtn={`btn-color-inverted ${projectType === (EProjectType.Macra2 | EProjectType.SlopedWeir) ? 'active' : ''}`}
                            onClick={() => handleChangeType(EProjectType.Macra2 | EProjectType.SlopedWeir)}>
                            {t('project_type_structure_sloped_weir')}
                        </Button>
                    </div>
                )}

                <div className='d-flex justify-content-between align-items-center py-4'>
                    <label className='fs-5'>{t('template_available_templates')}</label>
                    <Dropdown title={t('unit_system_title')} name='UnitOfMeasure' className='d-flex align-items-center gap-2' options={options} onChange={handleChangeDropdown} />
                </div>
                {!template.initialState && template.pending ? (
                    <div className='d-flex justify-content-center align-items-center '>
                        <Spinner />
                    </div>
                ) : (
                    <>{!!template.data && <TemplateProject onChange={handleChangeTemplate} templates={template.data!.data} />}</>
                )}
                <div className={divClasses}>
                    {page <= totalPages && totalPages !== 1 && <Pagination count={totalPages} page={page} onChange={(e, value) => handlePageChange(value)} />}
                    <Button disabled={template.pending} onClick={handleClick}>
                        {t('next_button')}
                    </Button>
                </div>
            </div>
        </Layout>
    );
};

export default Template;
